/**
 * Coup de pouce — Attendu 1059 « connaitre les unités d'aires et de volume ».
 * DEUX pages STATIQUES, partagées par les trois generator_keys, puces
 * « m², un carré » et « m³, un cube ». Phrases validées en texte nu par le
 * prof le 2026-09-09, codées mot pour mot.
 *
 * L'image que l'élève a déjà : le carré de 1 m pavé de 100 carrés de 1 dm
 * (fiche 1028, 6e), prolongé en cube de 1 m empilé par couches (cubes
 * empilés de 648, 649 et « cubes par couche × couches » de 948). Chaque
 * page : le dessin avec « 10 dm » sur les côtés et le petit carré (ou cube)
 * orange étiqueté 1 dm² (1 dm³), le calcul en gros dans l'ORDRE du RA16
 * cycle 4 Grandeurs et mesures p. 3 (« 1 m² = 1 m × 1 m = 10 dm × 10 dm =
 * 100 dm² » : le 1 m × 1 m d'abord, d'où vient le petit 2, vérifié sur
 * Eduscol le 2026-09-09), le résultat en couleur, puis UNE ligne du geste :
 * « m², c'est m × m : deux longueurs multipliées. » et « m³, c'est m × m ×
 * m : trois longueurs multipliées. » Mots : carré, cube, couche ; jamais
 * « arête », « profondeur », ni 10² / 10³.
 *
 * PAS de sacrifice (exemption comme 1047 : six faits à mémoriser, en retirer
 * un laisserait trop peu aux cartes) ; la fiche prend m/dm comme 1028, les
 * cartes le gardent. Largeur bornée à 340 px, tailles de 1048.
 */

const ORANGE = '#EA580C';
const ORANGE_SOMBRE = '#C2410C';
const ENCRE = '#1E293B';
const GRIS = '#94A3B8';
const GRIS_CLAIR = '#F1F5F9';

const svg = (aria, hauteur, corps) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 ${hauteur}" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="${aria}">
  ${corps}
</svg>`.trim();


// ─── Page 1 : m², un carré ────────────────────────────────────────────────

const C1 = 18;
const X1 = 110;
const Y1 = 40;
const GRILLE_CARRE = Array.from({ length: 100 }, (_, k) => {
    const l = Math.floor(k / 10);
    const c = k % 10;
    const orange = l === 0 && c === 0;
    return `<rect x="${X1 + c * C1}" y="${Y1 + l * C1}" width="${C1}" height="${C1}" fill="${orange ? ORANGE : GRIS_CLAIR}" stroke="${GRIS}" stroke-width="0.6"/>`;
}).join('\n  ');

const SVG_CARRE = svg(
    'Un carré de 1 m, 10 dm de côté, quadrillé en 10 sur 10 ; le petit carré orange en haut à gauche est étiqueté 1 dm². 1 m² égale 1 m fois 1 m, égale 10 dm fois 10 dm, égale 100 dm². m², c\'est m fois m : deux longueurs multipliées.',
    322,
    `<!-- 1. le carré de 1 m quadrillé -->
  ${GRILLE_CARRE}
  <rect x="${X1}" y="${Y1}" width="${10 * C1}" height="${10 * C1}" fill="none" stroke="${ENCRE}" stroke-width="1.6"/>
  <text x="${X1 + 5 * C1}" y="${Y1 + 10 * C1 + 20}" font-size="13" font-weight="bold" text-anchor="middle" fill="${ENCRE}">10 dm</text>
  <text x="${X1 - 14}" y="${Y1 + 5 * C1}" font-size="13" font-weight="bold" text-anchor="middle" fill="${ENCRE}" transform="rotate(-90 ${X1 - 14} ${Y1 + 5 * C1})">10 dm</text>
  <text x="${X1 + C1 + 6}" y="${Y1 - 8}" font-size="12" font-weight="bold" fill="${ORANGE}">1 dm²</text>

  <!-- 2. le calcul, dans l'ordre du RA16 : 1 m × 1 m d'abord -->
  <text x="200" y="266" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">1 m² = 1 m × 1 m</text>
  <text x="200" y="290" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">= 10 dm × 10 dm = <tspan fill="${ORANGE}">100 dm²</tspan></text>

  <!-- 3. le geste : d'où vient le petit 2 -->
  <text x="200" y="314" font-size="15" text-anchor="middle" fill="${ENCRE}">m², c'est m × m : deux longueurs multipliées.</text>`,
);

// ─── Page 2 : m³, un cube ─────────────────────────────────────────────────

const C2 = 16;
const X2 = 90;
const Y2 = 84;
const DX = 50;
const DY = -40;
const F = 10 * C2;

/** La face du dessus quadrillée en 10 × 10 (parallélogramme), la case avant-gauche en orange. */
const FACE_DESSUS = [
    ...Array.from({ length: 11 }, (_, k) => `<line x1="${X2 + k * C2}" y1="${Y2}" x2="${X2 + k * C2 + DX}" y2="${Y2 + DY}" stroke="${GRIS}" stroke-width="0.6"/>`),
    ...Array.from({ length: 11 }, (_, k) => `<line x1="${X2 + (k * DX) / 10}" y1="${Y2 + (k * DY) / 10}" x2="${X2 + F + (k * DX) / 10}" y2="${Y2 + (k * DY) / 10}" stroke="${GRIS}" stroke-width="0.6"/>`),
].join('\n  ');
/** Les 10 couches, sur la face avant et la face de droite. */
const COUCHES = [
    ...Array.from({ length: 11 }, (_, k) => `<line x1="${X2}" y1="${Y2 + k * C2}" x2="${X2 + F}" y2="${Y2 + k * C2}" stroke="${GRIS}" stroke-width="0.6"/>`),
    ...Array.from({ length: 11 }, (_, k) => `<line x1="${X2 + F}" y1="${Y2 + k * C2}" x2="${X2 + F + DX}" y2="${Y2 + k * C2 + DY}" stroke="${GRIS}" stroke-width="0.6"/>`),
].join('\n  ');
const dx1 = DX / 10;
const dy1 = DY / 10;

const SVG_CUBE = svg(
    'Un cube de 1 m, 10 dm de côté ; la face du dessus est quadrillée en 10 sur 10, dix couches sont visibles sur les faces ; le petit cube orange devant, en haut à gauche, est étiqueté 1 dm³. 1 m³ égale 1 m fois 1 m fois 1 m, égale 10 dm fois 10 dm fois 10 dm, égale 1 000 dm³. m³, c\'est m fois m fois m : trois longueurs multipliées.',
    334,
    `<!-- 1. le cube de 1 m : face avant, face de droite, face du dessus -->
  <rect x="${X2}" y="${Y2}" width="${F}" height="${F}" fill="${GRIS_CLAIR}" stroke="${ENCRE}" stroke-width="1.6"/>
  <polygon points="${X2 + F},${Y2} ${X2 + F + DX},${Y2 + DY} ${X2 + F + DX},${Y2 + DY + F} ${X2 + F},${Y2 + F}" fill="#E2E8F0" stroke="${ENCRE}" stroke-width="1.6"/>
  <polygon points="${X2},${Y2} ${X2 + DX},${Y2 + DY} ${X2 + F + DX},${Y2 + DY} ${X2 + F},${Y2}" fill="#FFFFFF" stroke="${ENCRE}" stroke-width="1.6"/>
  ${FACE_DESSUS}
  ${COUCHES}
  <!-- le petit cube orange, devant en haut à gauche -->
  <rect x="${X2}" y="${Y2}" width="${C2}" height="${C2}" fill="${ORANGE}" stroke="#FFFFFF" stroke-width="0.8"/>
  <polygon points="${X2},${Y2} ${X2 + dx1},${Y2 + dy1} ${X2 + C2 + dx1},${Y2 + dy1} ${X2 + C2},${Y2}" fill="${ORANGE_SOMBRE}" stroke="#FFFFFF" stroke-width="0.8"/>
  <polygon points="${X2 + C2},${Y2} ${X2 + C2 + dx1},${Y2 + dy1} ${X2 + C2 + dx1},${Y2 + dy1 + C2} ${X2 + C2},${Y2 + C2}" fill="${ORANGE_SOMBRE}" stroke="#FFFFFF" stroke-width="0.8"/>
  <text x="${X2 - 6}" y="${Y2 + 12}" font-size="12" font-weight="bold" text-anchor="end" fill="${ORANGE}">1 dm³</text>
  <!-- les côtés : 10 dm -->
  <text x="${X2 + F / 2}" y="${Y2 + F + 20}" font-size="13" font-weight="bold" text-anchor="middle" fill="${ENCRE}">10 dm</text>
  <text x="${X2 + F + DX + 16}" y="${Y2 + DY + F / 2 + 4}" font-size="13" font-weight="bold" text-anchor="middle" fill="${ENCRE}" transform="rotate(-90 ${X2 + F + DX + 16} ${Y2 + DY + F / 2})">10 dm</text>
  <text x="${X2 + F + DX / 2 + 22}" y="${Y2 + DY / 2 - 2}" font-size="13" font-weight="bold" fill="${ENCRE}">10 dm</text>

  <!-- 2. le calcul, dans l'ordre du RA16 : 1 m × 1 m × 1 m d'abord -->
  <text x="200" y="278" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">1 m³ = 1 m × 1 m × 1 m</text>
  <text x="200" y="302" font-size="15" font-weight="bold" text-anchor="middle" fill="${ENCRE}">= 10 dm × 10 dm × 10 dm = <tspan fill="${ORANGE}">1 000 dm³</tspan></text>

  <!-- 3. le geste : d'où vient le petit 3 -->
  <text x="200" y="326" font-size="15" text-anchor="middle" fill="${ENCRE}">m³, c'est m × m × m : trois longueurs multipliées.</text>`,
);

const AIDE = {
    pages: [
        { titre: 'm², un carré', svg: SVG_CARRE },
        { titre: 'm³, un cube', svg: SVG_CUBE },
    ],
};

const CP_1059 = {
    flash_texte_trou_saisie: AIDE,
    carre_qcm_unique: AIDE,
    carre_tableau_vf: AIDE,
};

export default CP_1059;
